import React, { FC, useEffect, useState } from 'react'

import styled from 'styled-components'

import { AddUserButton, TableSortType, NotExistsUserMessage } from 'src/components/molecules'
import { BulkActionButton } from 'src/components/molecules/inputs/BulkActionButton'
import {
  CreateExternalUserModal,
  ExternalUserListBody,
  ExternalUserListHead,
  ExternalUserSortItemType,
  UsersBulkAssignModal,
} from 'src/components/organisms'

import { useAuthority } from 'src/hooks'
import { ExternalUserEntity, SelectableExternalUserEntity } from 'src/types'

const Container = styled.div`
  border-radius: 0 8px 8px 8px;
  filter: drop-shadow(0px 5px 20px rgba(122, 137, 160, 0.1));
  overflow: hidden;
`

type Props = {
  externalUsers: ExternalUserEntity[]
  sortItem: ExternalUserSortItemType
  sortType: TableSortType
  selectSortItem: (newItem: ExternalUserSortItemType) => void
  selectSortType: (newType: TableSortType) => void
  afterOnSort?: () => void
  className?: string
}

export const ExternalUserListTable: FC<Props> = ({
  externalUsers,
  sortItem,
  sortType,
  selectSortItem,
  selectSortType,
  afterOnSort,
  className,
}) => {
  const [selectableExternalUsers, setSelectableExternalUsers] = useState<
    SelectableExternalUserEntity[]
  >(externalUsers.map(user => ({ ...user, isSelected: false })))
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false)

  const { canManageUser } = useAuthority()

  const handleOnAllSelect = () => {
    const isAllSelected = selectableExternalUsers.every(user => user.isSelected)
    const updatedInternalUsers = selectableExternalUsers.map(user => {
      // 検索対象にないユーザーは無視する
      return {
        ...user,
        isSelected: !isAllSelected,
      }
    })
    setSelectableExternalUsers(updatedInternalUsers)
  }

  const handleOnSelectUser = (selectedExternalUser: SelectableExternalUserEntity) => {
    const updatedExternalUsers = selectableExternalUsers.map(user => {
      return user.id === selectedExternalUser.id
        ? {
            ...user,
            isSelected: !selectedExternalUser.isSelected,
          }
        : user
    })
    setSelectableExternalUsers(updatedExternalUsers)
  }

  const handleSelectSortItem = (newItem: ExternalUserSortItemType) => {
    if (sortItem === newItem) {
      const newSortType = sortType === 'desc' ? 'asc' : 'desc'
      selectSortType(newSortType)
      return
    }

    selectSortType('desc')
    selectSortItem(newItem)
    afterOnSort && afterOnSort()
  }

  const handleAfterAssigned = () => {
    setShowBulkAssignModal(false)
    setSelectableExternalUsers(previousUsers => {
      return previousUsers.map(user => ({ ...user, isSelected: false }))
    })
  }

  useEffect(() => {
    const updatedUsers = externalUsers.map(user => ({ ...user, isSelected: false }))
    setSelectableExternalUsers(updatedUsers)
  }, [externalUsers])

  return (
    <div className={className}>
      <Container className="tw-border tw-border-border-secondary">
        <ExternalUserListHead
          externalUsers={selectableExternalUsers}
          sortItem={sortItem}
          sortType={sortType}
          onSelectItem={handleSelectSortItem}
          onClickAllSelect={handleOnAllSelect}
          className="tw-border-b tw-border-b-border-secondary"
        />
        {canManageUser && (
          <div className="tw-p-s0 tw-bg-surface-primary">
            <AddUserButton
              type="external"
              onClick={() => setShowCreateModal(true)}
              className="tw-w-full"
            />
          </div>
        )}
        {selectableExternalUsers.length > 0 ? (
          <ExternalUserListBody
            externalUsers={selectableExternalUsers}
            onSelectUser={handleOnSelectUser}
          />
        ) : (
          <NotExistsUserMessage />
        )}
      </Container>

      <CreateExternalUserModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />

      {showBulkAssignModal && (
        <UsersBulkAssignModal
          targetUserIds={selectableExternalUsers
            .filter(user => user.isSelected)
            .map(user => user.id)}
          isExternal
          isOpen={showBulkAssignModal}
          onClose={() => setShowBulkAssignModal(false)}
          afterAssigned={handleAfterAssigned}
        />
      )}

      <BulkActionButton
        isActive={selectableExternalUsers.some(user => user.isSelected)}
        actions={[
          {
            text: 'プロジェクト追加',
            iconPath: 'plus/soft/line',
            action: () => setShowBulkAssignModal(true),
          },
        ]}
      />
    </div>
  )
}
